// EGY SZÁMLA a havi áttekintésben — összecsukott fejléc (szám / „Előkészítés alatt", státusz, dátumok, összeg,
// PDF) + kibontható TÉTEL-lista és számla-megjegyzés. A tételek a havi válaszban már benne vannak (nincs külön
// kérés). A piszkozat TÉNY (a partner látja, hogy készül), de PDF nélkül és „Előkészítés alatt" felirattal.
import type { PortalInvoiceDetail } from '@atlas/contracts';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InvoiceStatusBadge } from '@/components/invoices/invoice-status-badge';
import { PortalCard, PortalCardTitle } from '@/components/portal/portal-card';
import { PortalInvoiceItems } from '@/components/portal/portal-invoice-items';
import { PortalInvoiceScope } from '@/components/portal/portal-invoice-scope';
import { PortalSharedBadge } from '@/components/portal/portal-shared-badge';
import { MoneyBlock } from '@/components/shared/money-block';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { formatDay } from '@/features/portal/billing-format';
import { cn } from '@/lib/utils';

export function PortalInvoiceCard({
  invoice,
  downloading,
  onDownload,
}: {
  invoice: PortalInvoiceDetail;
  downloading: boolean;
  onDownload: (id: string) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const isDraft = invoice.status === 'draft';
  const title = isDraft ? t('portal.billing.invoice.draft') : invoice.invoiceNumber;
  const panelId = `portal-invoice-${invoice.id}`;

  return (
    <PortalCard
      as="li"
      data-testid="portal-invoice-card"
      title={<PortalCardTitle mono>{title}</PortalCardTitle>}
      amount={
        <MoneyBlock
          gross={invoice.gross}
          net={invoice.net}
          vat={invoice.vat}
          currency={invoice.currency}
          className="sm:items-end"
        />
      }
      badges={
        <>
          <InvoiceStatusBadge
            status={invoice.status}
            label={
              isDraft
                ? t('portal.billing.invoice.draft')
                : t(`portal.invoices.status.${invoice.status}`, { defaultValue: invoice.status })
            }
          />
          <PortalInvoiceScope accountName={invoice.accountName} badgeOnly />
          <PortalSharedBadge shared={invoice.shared} />
        </>
      }
      meta={
        <>
          {invoice.accountName !== null && (
            <Text variant="secondary" as="p">
              {invoice.accountName}
            </Text>
          )}
          {isDraft ? (
            <Text variant="secondary" as="p">
              {t('portal.billing.invoice.draftHint')}
            </Text>
          ) : (
            <Text variant="secondary" as="p">
              {t('portal.invoices.issued')}{' '}
              <span className="font-mono tabular-nums">
                {formatDay(invoice.issueDate, i18n.language)}
              </span>{' '}
              · {t('portal.invoices.due')}{' '}
              <span
                className={cn(
                  'font-mono tabular-nums',
                  invoice.status === 'overdue' && 'text-danger-600',
                )}
              >
                {formatDay(invoice.dueDate, i18n.language)}
              </span>
            </Text>
          )}
        </>
      }
    >
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
          className="text-brand hover:text-brand-dark inline-flex items-center gap-1 text-xs font-medium focus-visible:outline-none"
        >
          <ChevronDownIcon className={cn('size-4 transition-transform', open && 'rotate-180')} />
          {open ? t('portal.billing.invoice.hideItems') : t('portal.billing.invoice.showItems')}
        </button>
        <Button
          variant="outline"
          size="sm"
          disabled={!invoice.canDownloadPdf || downloading}
          aria-label={t('portal.invoices.downloadAria', { number: invoice.invoiceNumber })}
          onClick={() => onDownload(invoice.id)}
        >
          <ArrowDownTrayIcon />
          PDF
        </Button>
      </div>
      {open && (
        <div id={panelId}>
          <PortalInvoiceItems invoice={invoice} />
        </div>
      )}
    </PortalCard>
  );
}
